<template>
  <div class="empty-wrap"
    :class="{ 'tg-drag-over': isDragOver }"
    @dragenter.prevent="isDragOver = true"
    @dragover.prevent
    @dragleave="onDragLeave"
    @drop.prevent="handleDrop"
  >
    <div class="hero-copy">
      <h1 class="hero-title">你的全能工作伙伴</h1>
      <p class="hero-desc">随时随地召唤，7×24 小时运行。把产品、运营、分析、创作这类高频工作沉淀成可复用能力。</p>
    </div>

    <!-- Attachment overlay -->
    <div v-if="pendingAttachments.length" class="tg-attach-overlay">
      <div class="tg-attach-panel">
        <div class="tg-attach-head">
          <span class="tg-attach-title">发送文件
            <span v-if="pendingAttachments.length > 1" class="tg-attach-count">{{ pendingAttachments.length }} 个</span>
          </span>
          <div class="tg-attach-head-actions">
            <button class="tg-attach-add-btn" type="button" title="继续添加文件" @click="triggerFileInput">
              <svg viewBox="0 0 24 24" fill="none" width="18" height="18"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
            <button class="tg-attach-close" type="button" @click="cancelAttachment">
              <svg viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
          </div>
        </div>
        <div class="tg-attach-grid">
          <div v-for="(att, i) in pendingAttachments" :key="i" class="tg-attach-grid-item">
            <img v-if="att.kind === 'image'" :src="att.localUrl" class="tg-attach-grid-img" />
            <video v-else-if="att.kind === 'video'" :src="att.localUrl" class="tg-attach-grid-video" muted playsinline @loadedmetadata="e => { e.target.currentTime = 0.1 }"></video>
            <div v-else class="tg-attach-grid-doc">
              <div class="tg-attach-grid-doc-icon">{{ fileKindLabel(att) }}</div>
              <div class="tg-attach-grid-doc-name">{{ att.fileName }}</div>
              <div class="tg-attach-grid-doc-size">{{ att.fileSizeStr }}</div>
            </div>
              <div v-if="attachUploading" class="tg-attach-progress-overlay">
                <div class="tg-attach-progress-ring">
                  <svg viewBox="0 0 36 36" width="36" height="36">
                    <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="3"/>
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#fff" stroke-width="3"
                      stroke-linecap="round" :stroke-dasharray="87.96"
                      :stroke-dashoffset="87.96 * (1 - (uploadProgress[i] || 0) / 100)"
                      transform="rotate(-90 18 18)"/>
                  </svg>
                  <span class="tg-attach-progress-pct">{{ uploadProgress[i] || 0 }}%</span>
                </div>
              </div>
              <button v-if="!attachUploading" class="tg-attach-grid-remove" type="button" @click="removeAttachment(i)">
                <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
          <div class="tg-attach-caption-row">
            <textarea class="tg-attach-caption" placeholder="添加说明（可选）..."
              v-model="attachCaption"
              @keydown.enter.exact.prevent="sendAttachment"
              @keydown.shift.enter.exact="attachCaption += '\n'"
              rows="1" ref="captionInput"
              :disabled="attachUploading"
            ></textarea>
            <button class="tg-attach-send-btn" :class="{ uploading: attachUploading }" type="button" @click="sendAttachment" :disabled="attachUploading">
              <template v-if="attachUploading">
                <svg viewBox="0 0 36 36" width="22" height="22">
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="3"/>
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#fff" stroke-width="3"
                    stroke-linecap="round" :stroke-dasharray="87.96"
                    :stroke-dashoffset="87.96 * (1 - totalUploadProgress / 100)"
                    transform="rotate(-90 18 18)"/>
                </svg>
              </template>
              <svg v-else viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 2L15 22l-4-9-9-4 18-7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
      </div>
    </div>

    <!-- Agent 已停用：替代 prompt-box 显示禁用提示，所有输入控件不渲染 -->
    <div v-if="isCurrentAgentDisabled" class="prompt-box prompt-box-disabled">
      <svg viewBox="0 0 24 24" fill="none" style="width:16px;height:16px;flex-shrink:0"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M5.64 5.64l12.72 12.72" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
      <span>该 Agent 已停用，无法发送消息。仅可查看历史会话与交付物。</span>
    </div>
    <div v-else class="prompt-box">
      <div class="prompt-editor" style="position:relative;">
        <div v-if="voiceActive" class="voice-recording-panel">
          <template v-if="voiceError">
            <div class="voice-recording-error">{{ voiceError }}</div>
            <button class="voice-recording-stop" type="button" @click="voiceActive = false; voiceError = ''">关闭</button>
          </template>
          <template v-else>
            <div class="voice-recording-indicator"><span class="voice-recording-dot"></span><span class="voice-recording-label">正在聆听...</span></div>
            <div v-if="voiceText" class="voice-recording-result">{{ voiceText }}</div>
            <button class="voice-recording-stop" type="button" @click="toggleVoice">停止录入</button>
          </template>
        </div>
        <textarea v-else
          id="emptyComposerInput"
          :placeholder="'给' + (currentAgentProfile ? currentAgentProfile.name : 'Agent') + '发消息'"
          :value="inputValue"
          @input="onInput"
          @keydown="onKeydown"
          ref="textarea"
        ></textarea>
      </div>
      <div class="composer-feedback-row" data-skill-feedback="empty" :hidden="selectedSkills.length === 0 && !selectedCommand">
        <span v-if="selectedCommand" class="composer-cmd-chip">
          <span class="composer-cmd-chip-text">{{ selectedCommand }}</span>
          <button class="composer-cmd-chip-remove" type="button" @click="$store.commit('SET_SESSION_COMMAND', null)">×</button>
        </span>
        <span v-for="skill in selectedSkills" :key="skill.id" class="composer-skill-chip">
          <span class="composer-skill-chip-icon">
            <img v-if="isUrlIcon(skill.icon)" :src="skill.icon" alt="" @error="onIconError" />
            <span v-else>{{ skill.icon }}</span>
          </span>
          <span>{{ skill.name }}</span>
          <button class="composer-skill-chip-remove" type="button" @click="removeSkill(skill.id)">×</button>
        </span>
      </div>
      <div class="tool-row">
        <div class="tool-left">
          <button class="tool-btn" type="button" @click="triggerFileInput">
            <svg viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            <span class="tool-btn-label">添加文件</span>
          </button>
          <input type="file" ref="fileInput" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.zip,.rar,.7z" style="display:none" multiple @change="handleFileSelect" />
          <button class="tool-btn" :class="{ 'skill-selected': cmdPickerOpen || !!selectedCommand, 'tool-btn-disabled': isSkillMode }" :disabled="isSkillMode" type="button" @click.stop="openCmdPicker" :title="isSkillMode ? '使用技能时不可选择命令' : '命令'">
            <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M8 9l3-3 3 3M8 15l3 3 3-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span class="tool-btn-label">命令</span>
            <span class="composer-model-btn-caret">▾</span>
          </button>
          <CommandPickerPopover
            :open="cmdPickerOpen"
            :trigger-rect="cmdTriggerRect"
            @select="insertCommand"
            @close="cmdPickerOpen = false"
          />
          <button class="tool-btn" :class="{ 'skill-selected': selectedSkills.length > 0, 'tool-btn-disabled': isCommandMode }" :disabled="isCommandMode" type="button" data-skill-trigger="empty" @click="openSkillPicker" :title="isCommandMode ? '使用命令时不可选择技能' : '使用技能'">
            <svg viewBox="0 0 24 24" fill="none"><path d="M12 3l2.4 4.86 5.36.78-3.88 3.78.92 5.34L12 15.77 7.2 17.76l.92-5.34L4.24 8.64l5.36-.78L12 3z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path></svg>
            <span class="tool-btn-label">使用技能</span>
            <span v-if="selectedSkills.length > 0" class="tool-btn-clear" data-skill-clear-inline aria-label="取消选中" @click.stop="clearAllSkills">
              <svg viewBox="0 0 24 24" fill="none"><path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>
            </span>
          </button>
          <button class="tool-btn composer-model-btn" :class="{ 'tool-btn-disabled': isCommandMode }" :disabled="isCommandMode" type="button" data-model-trigger="empty" @click="!isCommandMode && openModelPicker($event)" :title="isCommandMode ? '命令模式下不可切换模型' : ''">
            <span class="tool-btn-label tool-btn-model-label">{{ selectedModel }}</span>
            <span class="composer-model-btn-caret">▾</span>
          </button>
        </div>
        <div class="tool-right">
          <!-- <button class="tool-btn icon-only" :class="{ 'voice-active': voiceActive }" title="语音输入" @click="toggleVoice">
            <svg viewBox="0 0 24 24" fill="none"><path d="M12 3a3 3 0 013 3v6a3 3 0 11-6 0V6a3 3 0 013-3zM19 11a7 7 0 01-14 0M12 18v3M8 21h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
          </button> -->
          <button class="send-btn" id="emptyComposerSend" title="发送" @click="send">
            <svg viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path><path d="M22 2L15 22l-4-9-9-4 18-7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
          </button>
        </div>
      </div>
    </div>

    <section v-if="displaySkills.length" class="skills-showcase">
      <div class="skills-showcase-grid">
        <article v-for="(sk, si) in displaySkills" :key="sk.id || si" class="skills-feature-card" :class="['tone-' + toneColors[si % toneColors.length]]" @click="selectSkillCard(sk)">
          <img v-if="skillIconUrl(sk)" :src="skillIconUrl(sk)" class="skills-feature-img" @error="onIconError" />
          <div v-else class="skills-feature-icon">{{ sk.icon || '🔧' }}</div>
          <div class="skills-feature-title">{{ sk.name || sk.skillName || '' }}</div>
          <div class="skills-feature-desc">{{ sk.skillDescription || '' }}</div>
        </article>
      </div>
    </section>
    <ModelPickerPopover />
    <SkillPickerPopover />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { api } from '../utils/api'
import { isSpeechSupported, getSpeechUnsupportedReason, startSpeechRecognition, stopSpeechRecognition } from '../utils/speech'
import ModelPickerPopover from './ModelPickerPopover.vue'
import SkillPickerPopover from './SkillPickerPopover.vue'
import CommandPickerPopover from './CommandPickerPopover.vue'

function _getImageDimensions(url) {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => resolve({ width: 0, height: 0 })
    img.src = url
  })
}
function _preloadImage(url) {
  return new Promise(resolve => { const img = new Image(); img.onload = resolve; img.onerror = resolve; img.src = url })
}

export default {
  name: 'AgentStateEmpty',
  components: { ModelPickerPopover, SkillPickerPopover, CommandPickerPopover },
  data() {
    return {
      pendingAttachments: [],
      attachCaption: '',
      attachUploading: false,
      isDragOver: false,
      voiceActive: false,
      voiceText: '',
      voiceError: '',
      _voiceBaseText: '',
      toneColors: ['gold', 'red', 'blue'],
      uploadProgress: [],
      cmdPickerOpen: false,
      cmdTriggerRect: null,
    }
  },
  computed: {
    ...mapState(['composerInputValues', 'currentSessionKey', 'currentTopicKey']),
    ...mapGetters(['selectedModel', 'selectedSkills', 'selectedCommand', 'currentAgentProfile', 'currentConversationId', 'currentSession']),
    // 1对1 Agent 已停用 → 禁用整个 prompt-box(技能/命令/模型/附件/文本框/发送全部不渲染)
    isCurrentAgentDisabled() {
      const s = this.currentSession
      return !!(s && s.type === 'agent' && s.myAgentStatus === 'disabled')
    },
    totalUploadProgress() {
      if (!this.uploadProgress.length) return 0
      return Math.round(this.uploadProgress.reduce((a, b) => a + b, 0) / this.uploadProgress.length)
    },
    composerContext() {
      const topicKey = this.currentTopicKey
      const sessionKey = this.currentSessionKey || 'unknown'
      return topicKey ? (sessionKey + '-empty-' + topicKey) : (sessionKey + '-empty')
    },
    inputValue() { return this.composerInputValues[this.composerContext] || '' },
    isCommandMode() { return this.inputValue.startsWith('/') || !!this.selectedCommand },
    isSkillMode() { return this.selectedSkills.length > 0 },
    displaySkills() {
      // 优先从 conversationAgents 的 agent skills 取
      const convId = this.currentConversationId
      if (convId) {
        const agents = this.$store.state.conversationAgents[convId] || []
        for (let i = 0; i < agents.length; i++) {
          const skills = agents[i].skills || agents[i].roleSkills || []
          if (skills.length) {
            return skills.slice(0, 6).map(s => typeof s === 'string' ? { name: s } : s)
          }
        }
      }
      // fallback 到 agentSkillList (capabilities 接口)
      if (this.$store.state.agentSkillList.length) {
        return this.$store.state.agentSkillList.slice(0, 6)
      }
      return []
    }
  },
  watch: {
    currentSessionKey() { this.$nextTick(() => this.focusTextarea()) },
    currentTopicKey() { this.$nextTick(() => this.focusTextarea()) },
  },
  mounted() {
    this.$nextTick(() => this.focusTextarea())
  },
  methods: {
    focusTextarea() {
      const ta = this.$refs.textarea
      if (ta) ta.focus()
    },
    // skill.icon 后端要么 URL（http/https/data/blob/相对路径 /），要么 emoji/单字
    isUrlIcon(s) { return typeof s === 'string' && /^(https?:|data:|blob:|\/)/.test(s) },
    // 兼容两种字段：skillIcon 永远是 URL；icon 是 store 折叠后字段，可能是 URL 也可能是 emoji
    skillIconUrl(sk) {
      if (!sk) return ''
      if (sk.skillIcon) return sk.skillIcon
      if (this.isUrlIcon(sk.icon)) return sk.icon
      return ''
    },
    onIconError(e) { try { e.target.style.display = 'none' } catch (_) {} },
    ...mapMutations(['SET_COMPOSER_INPUT', 'CLEAR_SKILLS', 'TOGGLE_SKILL']),
    ...mapActions(['sendConversationMessage']),
    onInput(e) {
      const val = e.target.value
      this.SET_COMPOSER_INPUT({ context: this.composerContext, value: val })
      // 如果用户删掉了命令前缀，清空命令
this.autoResize()
    },
    onKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send() }
    },
    send() {
      const val = this.inputValue.trim()
      if (!val && !this.selectedCommand) return
      const convId = this.currentConversationId
      if (!convId) return
      // 先清空输入框
      this.SET_COMPOSER_INPUT({ context: this.composerContext, value: '' })
      // 拼上命令
      const cmd = this.selectedCommand
      const bubbleText = cmd ? (val ? `${cmd} ${val}` : cmd) : val
      // 立即构建乐观消息并提交到 store，再切换状态
      const tempMsg = {
        id: 'temp-' + Date.now(),
        side: 'right',
        name: '',
        text: bubbleText,
        skills: this.selectedSkills.map(s => ({ id: s.id, name: s.name })),
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
        avatarText: this.auth && this.auth.name ? this.auth.name.slice(0, 1) : 'Me',
        avatarImage: (this.auth && this.auth.avatarUrl) || null,
        avatarColorRgb: (this.auth && this.auth.avatarColorRgb) || '',
        kind: 'text'
      }
      this.$emit('message-sent')
      this.$store.commit('SET_MAIN_STATE', 'processing')
      // 统一由 store 处理乐观消息和发送
      this.sendConversationMessage({ conversationId: convId, text: val })
      this.$nextTick(() => this.autoResize())
    },
    autoResize() {
      const ta = this.$refs.textarea
      if (!ta) return
      const lh = parseFloat(window.getComputedStyle(ta).lineHeight) || 24
      ta.style.height = 'auto'
      ta.style.height = Math.min(Math.max(ta.scrollHeight, lh * 2), lh * 10) + 'px'
    },
    openCmdPicker(e) {
      if (this.isSkillMode) return
      e.stopPropagation()
      this.$store.commit('SET_SKILL_PICKER_OPEN', false)
      this.$store.commit('SET_MODEL_PICKER_OPEN', false)
      this.cmdTriggerRect = e.currentTarget.getBoundingClientRect()
      this.cmdPickerOpen = !this.cmdPickerOpen
    },
    insertCommand(cmd) {
      this.CLEAR_SKILLS()
      this.cmdPickerOpen = false
      const convId = this.currentConversationId
      if (convId) {
        this.sendConversationMessage({ conversationId: convId, text: cmd, skipOptimistic: false })
      }
    },
    selectSkillCard(sk) {
      const skillId = sk.id || sk.name
      if (!skillId) return
      // 只添加不移除，避免点击取消
      if (!this.$store.getters.selectedSkillIds.includes(skillId)) {
        this.TOGGLE_SKILL(skillId)
      }
    },
    toggleVoice() {
      if (this.voiceActive) {
        stopSpeechRecognition(); this.voiceActive = false
        if (this.voiceText) this.SET_COMPOSER_INPUT({ context: this.composerContext, value: this._voiceBaseText + this.voiceText })
        this.voiceText = ''; this.voiceError = ''; return
      }
      const reason = getSpeechUnsupportedReason()
      if (reason) { this.voiceActive = true; this.voiceError = reason; return }
      this.voiceActive = true; this.voiceText = ''; this.voiceError = ''; this._voiceBaseText = this.inputValue || ''
      startSpeechRecognition(
        (text) => { this.voiceText = text },
        () => { if (this.voiceText) this.SET_COMPOSER_INPUT({ context: this.composerContext, value: this._voiceBaseText + this.voiceText }); this.voiceActive = false; this.voiceText = ''; this.voiceError = '' },
        (err) => { this.voiceError = err || '语音识别失败'; this.voiceText = '' }
      )
    },
    openSkillPicker(e) {
      if (this.isCommandMode) return
      e.stopPropagation()
      this.cmdPickerOpen = false
      this.$store.commit('SET_MODEL_PICKER_OPEN', false)
      this.$store.commit('SET_SKILL_PICKER_OPEN', { open: true, rect: e.currentTarget.getBoundingClientRect() })
    },
    openModelPicker(e) {
      e.stopPropagation()
      this.cmdPickerOpen = false
      this.$store.commit('SET_SKILL_PICKER_OPEN', false)
      this.$store.commit('SET_MODEL_PICKER_OPEN', { open: true, rect: e.currentTarget.getBoundingClientRect() })
    },
    removeSkill(id) { this.CLEAR_SKILLS(id) },
    clearAllSkills() { this.CLEAR_SKILLS() },
    // ── 附件逻辑 ──────────────────────────────────────
    _fileToAttachment(file) {
      const mimeType = file.type || ''
      const ext = (file.name || '').split('.').pop().toLowerCase()
      let kind
      if (mimeType.startsWith('image/')) kind = 'image'
      else if (mimeType.startsWith('video/')) kind = 'video'
      else if (mimeType.startsWith('audio/')) kind = 'audio'
      else if (ext === 'md') kind = 'document'
      else if (['pdf','doc','docx','xls','xlsx','ppt','pptx'].includes(ext)) kind = 'document'
      else kind = 'file'
      const bytes = file.size
      const fileSizeStr = bytes > 0
        ? (bytes < 1024*1024 ? (bytes/1024).toFixed(1)+' KB' : (bytes/(1024*1024)).toFixed(1)+' MB')
        : ''
      const localUrl = (kind === 'image' || kind === 'video') ? URL.createObjectURL(file) : ''
      return { file, kind, localUrl, fileName: file.name || '未命名', fileSizeStr, mimeType }
    },
    _openAttachPanel(files) {
      if (!files || !files.length) return
      const currentText = this.inputValue || ''
      if (currentText.trim() && !this.attachCaption) {
        this.attachCaption = currentText
        this.SET_COMPOSER_INPUT({ context: this.composerContext, value: '' })
      }
      const newItems = Array.from(files).map(f => this._fileToAttachment(f))
      this.pendingAttachments = [...this.pendingAttachments, ...newItems]
      this.$nextTick(() => { if (this.$refs.captionInput) this.$refs.captionInput.focus() })
    },
    triggerFileInput() {
      const input = this.$refs.fileInput
      if (!input) return
      try { input.value = '' } catch (e) {}
      input.click()
    },
    handleFileSelect(e) {
      const fileArr = e.target.files ? Array.from(e.target.files) : []
      e.target.value = ''
      if (!fileArr.length) return
      this._openAttachPanel(fileArr)
    },
    onDragLeave(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) this.isDragOver = false
    },
    handleDrop(e) {
      this.isDragOver = false
      const files = e.dataTransfer && e.dataTransfer.files
      if (files && files.length) this._openAttachPanel(files)
    },
    handlePaste(e) {
      const items = e.clipboardData && e.clipboardData.items
      if (!items) return
      const fileItems = Array.from(items).filter(it => it.kind === 'file')
      if (!fileItems.length) return
      const files = fileItems.map(it => it.getAsFile()).filter(Boolean)
      if (!files.length) return
      e.preventDefault()
      e.stopPropagation()
      this._openAttachPanel(files)
    },
    removeAttachment(idx) {
      const att = this.pendingAttachments[idx]
      if (att && att.localUrl) URL.revokeObjectURL(att.localUrl)
      this.pendingAttachments.splice(idx, 1)
    },
    fileKindLabel(att) {
      const ext = (att.fileName || '').split('.').pop().toUpperCase()
      return ext || att.kind.toUpperCase() || '文'
    },
    cancelAttachment() {
      if (this.attachCaption.trim()) {
        this.SET_COMPOSER_INPUT({ context: this.composerContext, value: this.attachCaption })
      }
      this.pendingAttachments.forEach(att => { if (att.localUrl) URL.revokeObjectURL(att.localUrl) })
      this.pendingAttachments = []
      this.attachCaption = ''
    },
    async sendAttachment() {
      if (!this.pendingAttachments.length) return
      const attachments = [...this.pendingAttachments]
      const caption = this.attachCaption
      this.pendingAttachments = []
      this.attachCaption = ''
      const convId = this.currentConversationId
      try {
        if (convId) {
          const firstAtt = attachments[0]
          const tempId = 'temp-file-' + Date.now()
          const tempMsg = {
            id: tempId,
            side: 'right',
            name: '',
            text: caption || firstAtt.fileName || '文件',
            time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
            avatarText: this.auth && this.auth.name ? this.auth.name.slice(0, 1) : 'Me',
            avatarBackground: 'linear-gradient(135deg,#6F63FF,#5B8DEF)',
            kind: firstAtt.kind || 'file',
            fileName: firstAtt.fileName,
            imageUrl: firstAtt.kind === 'image' ? firstAtt.localUrl : '',
            status: 'sending',
            progress: 0
          }
          this.$store.commit('APPEND_CONVERSATION_MESSAGE', { conversationId: convId, message: tempMsg })
          this.$emit('message-sent')
          // 真实进度：各文件/分片 onProgress 按字节加权汇总（断网暂停 / 续传继续）
          const _sizes = attachments.map(a => (a.file && a.file.size) || 0)
          const _total = _sizes.reduce((a, b) => a + b, 0) || 1
          const _pct = new Array(attachments.length).fill(0)
          const _commitProgress = () => {
            let loaded = 0
            for (let i = 0; i < attachments.length; i++) loaded += (_pct[i] / 100) * _sizes[i]
            this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, progress: Math.min(99, Math.round(loaded / _total * 100)) })
          }
          const uploaded = await Promise.all(attachments.map(async (att, i) => {
            const onP = (p) => { _pct[i] = p; _commitProgress() }
            const result = att.kind === 'image' ? await api.uploadImage(att.file, onP) : await api.uploadFile(att.file, onP)
            const url = result.url || result.file_url || result.fileUrl || ''
            const item = { type: att.kind, name: att.fileName, url, size: att.file.size, mimeType: att.mimeType }
            if (att.kind === 'image' && att.localUrl) {
              try {
                const dim = await _getImageDimensions(att.localUrl)
                if (dim.width) { item.width = dim.width; item.height = dim.height }
              } catch {}
            }
            return item
          }))
          this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, progress: 99 })
          const sendResult = await api.sendMediaMessage(convId, {
            messageType: 'file',
            body: { files: uploaded, caption }
          })
          const realMsgId = sendResult && (sendResult.id || sendResult.messageId) ? (sendResult.id || sendResult.messageId) : tempMsg.id
          if (realMsgId !== tempMsg.id) {
            this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempMsg.id, id: realMsgId })
          }
          const firstUploaded = uploaded[0] || {}
          if (firstUploaded.url && attachments[0] && attachments[0].kind === 'image') await _preloadImage(firstUploaded.url)
          this.$store.commit('UPDATE_MESSAGE_STATUS', {
            conversationId: convId,
            messageId: realMsgId,
            status: 'sent',
            progress: undefined,
            imageUrl: firstUploaded.url || '',
            fileUrl: firstUploaded.url || ''
          })
          this.$nextTick(() => {
            this.$store.commit('SET_MAIN_STATE', 'processing')
          })
        }
      } catch (err) {
        console.warn('[AgentStateEmpty] 文件发送失败:', err.message)
        if (convId) {
          if (this.currentConversationId === convId) {
            this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, status: 'failed' })
          } else {
            // 已切走原会话，静默清掉 temp，避免切回看到陌生失败提示
            this.$store.commit('REMOVE_CONVERSATION_MESSAGE', { conversationId: convId, messageId: tempId })
          }
        }
      } finally {
        attachments.forEach(att => { if (att.localUrl) URL.revokeObjectURL(att.localUrl) })
      }
    }
  },
  mounted() {
    this.autoResize()
    if (this.$el) this.$el.addEventListener('paste', this.handlePaste)
  },
  beforeDestroy() {
    if (this.$el) this.$el.removeEventListener('paste', this.handlePaste)
  }
}
</script>
